import {
  SET_DOCUMENT_DATA_URL,
  UPDATE_DOCUMENT_PAGES_ORDER,
  REMOVE_DOCUMENT_PAGE,
  SET_DOCUMENT_FORM,
  SET_DOCUMENT_STATUS,
  RECEIVE_SAVED_DOCUMENTS,
  setScannedDocuments,
  RECEIVE_LIST_REQUESTED_DOCUMENTS,
  SET_DOCUMENT_REJECTED,
  UNSET_DOCUMENT_REJECTED,
  UPDATE_DOCUMENT_STATUS,
  SET_SUCCESS_SENT_KEYS,
  SET_DOCUMENTS_LIST_PAGE,
  SET_USERS_DATA,
  SET_SCANNED_DOCUMENTS
} from '../actions/rootAction';
import {
  RECEIVE_ESIGNATURE_DATA,
  RECEIVE_FINALISED_PDF_DATA
} from '../actions/esignature';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import { saveDocuments } from '../actions/rootAction';
import { scheduleStandaloneDraftSave } from '../../lib/standaloneDraftCache';

import { maxPagesCount } from '../utils';
import { getBase64FromUrl } from '../../lib/fileUtils';
import {
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_ID_CHECK,
  getRequestDocumentsInfo,
  isDocumentSubmitted,
  mapToArray
} from '../../components/helpers/utils';
import { gaeApp, gaeError } from '../../components/helpers/Analytics';
import { reactToRequestProcessAction } from '../requestProcessReactions';

const STANDALONE_DRAFT_ACTIONS = new Set([
  SET_DOCUMENT_DATA_URL,
  UPDATE_DOCUMENT_PAGES_ORDER,
  REMOVE_DOCUMENT_PAGE,
  SET_DOCUMENT_FORM,
  SET_DOCUMENT_STATUS,
  UPDATE_DOCUMENT_STATUS,
  SET_DOCUMENT_REJECTED,
  UNSET_DOCUMENT_REJECTED,
  SET_SUCCESS_SENT_KEYS,
  SET_DOCUMENTS_LIST_PAGE,
  SET_USERS_DATA,
  SET_SCANNED_DOCUMENTS,
  RECEIVE_ESIGNATURE_DATA,
  RECEIVE_FINALISED_PDF_DATA
]);

const shouldPersistStandaloneDraft = state =>
  !window.previewMode &&
  window.draftEnabled &&
  state.templateMode &&
  !state.templateRequestPrepared &&
  !state.scanEnded &&
  state.recipientRequestId;

const canScheduleDocumentDraftSave = state =>
  !window.previewMode && state.canSaveDocuments && !state.scanEnded;

/** Coalesce rapid form/status/page Redux actions into one intermediary put. */
const SAVE_DOCUMENTS_DEBOUNCE_MS = 1000;
let saveDocumentsTimer = null;

const scheduleSaveDocuments = store => {
  if (saveDocumentsTimer) {
    clearTimeout(saveDocumentsTimer);
  }
  saveDocumentsTimer = setTimeout(() => {
    saveDocumentsTimer = null;
    const state = store.getState();
    if (canScheduleDocumentDraftSave(state)) {
      gaeApp('SAVE_DOCUMENTS_STATE_TO_CACHE');
      store.dispatch(
        saveDocuments(
          state.recipientRequestId,
          state.requestedDocuments,
          state.scannedDocuments,
          state.forms,
          state.documentsStatus
        )
      );
    }
  }, SAVE_DOCUMENTS_DEBOUNCE_MS);
};

const scheduleDraftFromState = state => {
  scheduleStandaloneDraftSave(() => ({
    templateId: state.recipientRequestId,
    templateName: state.caseName,
    pwaSession: window.pwaSession,
    forms: state.forms,
    scannedDocuments: state.scannedDocuments,
    documentsStatus: state.documentsStatus,
    signatures: state.signatures,
    successSentKeys: state.successSentKeys,
    rejectedDocuments: state.rejectedDocuments,
    usersData: state.usersData
  }));
};

export const hooks = store => next => action => {
  const prevState = store.getState();

  switch (action.type) {
    case RECEIVE_LIST_REQUESTED_DOCUMENTS:
      if (action.requestedDocuments) {
        gaeApp(
          'REQUEST_RAW_INFO',
          `\n  ID: ${action.recipientRequestId}\n  STATE: ${action.state}\n\n` +
            getRequestDocumentsInfo(
              action.requestPages,
              action.requestedDocuments
            )
        );
      }
      break;
    //no default
  }

  //call reducer
  const result = next(action);

  const newState = store.getState();

  reactToRequestProcessAction(action, store.getState, store.dispatch);

  switch (action.type) {
    case RECEIVE_SAVED_DOCUMENTS:
      {
        const savedDocs = action.requestedDocuments;
        if (savedDocs) {
          gaeApp('RESTORE_SAVED_DOCUMENTS');
          const removeBrokenUrl = async () => {
            const scannedDocuments = new Map(newState.scannedDocuments);
            for (const doc of savedDocs) {
              const originalDoc = newState.requestedDocuments.get(doc.id);
              if (isDocumentSubmitted(originalDoc)) continue;

              switch (originalDoc.type) {
                case DOC_TYPE_DOCUMENT:
                case DOC_TYPE_ID_CHECK: {
                  const docPages = scannedDocuments.get(doc.id);
                  if (docPages && doc.attributes.attachments) {
                    for (const [
                      index,
                      pageId
                    ] of doc.attributes.attachments.entries()) {
                      const base64 = await getBase64FromUrl(
                        doc.attributes.attachmentUrls[index]
                      );
                      if (!base64) {
                        docPages.delete(pageId);
                      }
                    }
                  }
                }
                //no default
              }
            }
            return scannedDocuments;
          };
          removeBrokenUrl().then(scannedDocuments =>
            store.dispatch(setScannedDocuments(scannedDocuments))
          );
        }
      }
      break;

    case SET_DOCUMENT_DATA_URL:
      if (!window.previewMode && newState.canSaveDocuments) {
        gaeApp('SAVE_NEW_IMAGE_TO_CACHE');

        const scannedDocumentPages = prevState.scannedDocuments.get(
          action.documentId
        );

        const truncatedList = new Map();

        const uploadPages = nextPageKey => {
          const key = nextPageKey.next().value;
          if (key !== undefined) {
            const dataUrl = truncatedList.get(key);
            getBase64FromUrl(dataUrl).then(data => {
              PaperflyAPI.tempDataUploadPage(
                prevState.recipientRequestId,
                key,
                data
              ).then(
                () => {
                  uploadPages(nextPageKey);
                },
                err => {
                  console.error(err);
                  gaeError('ERROR_ON_SAVE_IMAGE_TO_CACHE');
                }
              );
            });
          } else {
            if (saveDocumentsTimer) {
              clearTimeout(saveDocumentsTimer);
              saveDocumentsTimer = null;
            }
            store.dispatch(
              saveDocuments(
                newState.recipientRequestId,
                newState.requestedDocuments,
                newState.scannedDocuments,
                newState.forms,
                newState.documentsStatus
              )
            );
          }
        };

        const allowItemsCount =
          maxPagesCount -
          (scannedDocumentPages ? scannedDocumentPages.size : 0);
        const preparedKeys = action.preparedPages.keys();
        while (truncatedList.size < allowItemsCount) {
          const key = preparedKeys.next().value;
          if (key === undefined) break;
          truncatedList.set(key, action.preparedPages.get(key));
        }
        uploadPages(truncatedList.keys());
      }
      break;
    case UPDATE_DOCUMENT_PAGES_ORDER:
    case REMOVE_DOCUMENT_PAGE:
    case SET_DOCUMENT_FORM:
    case SET_DOCUMENT_STATUS:
      if (canScheduleDocumentDraftSave(newState)) {
        scheduleSaveDocuments(store);
      }
      break;
    case RECEIVE_LIST_REQUESTED_DOCUMENTS:
      if (newState.requestedDocuments) {
        const requestedDocuments = mapToArray(newState.requestedDocuments);
        gaeApp(
          'REQUEST_REDUX_PREPARED_INFO',
          getRequestDocumentsInfo(newState.requestPages, requestedDocuments)
        );
      }
      break;
    //no default
  }

  if (
    STANDALONE_DRAFT_ACTIONS.has(action.type) &&
    shouldPersistStandaloneDraft(newState)
  ) {
    scheduleDraftFromState(newState);
  }

  return result;
};
